import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Mountain,
  CornerDownRight,
  Route,
  MapPin,
  Shield,
  Car,
  Sun,
  Moon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from 'next-themes';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();

  const features = [
    {
      icon: Mountain,
      title: 'Gradient Analysis',
      description: 'Real-time slope detection warns you of steep gradients that could challenge your vehicle.'
    },
    {
      icon: CornerDownRight,
      title: 'Bend Warnings',
      description: 'Advanced curve detection identifies sharp bends along your route before you reach them.'
    },
    {
      icon: Route,
      title: 'Narrow Road Detection',
      description: 'Route planning that considers road width for safe caravan and motorhome travel.'
    },
    {
      icon: MapPin,
      title: 'Points of Interest',
      description: 'Find rest areas, fuel stations, and scenic viewpoints tailored to your journey.'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div 
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, ${theme === 'dark' ? 'rgba(15, 17, 21, 0.7), rgba(15, 17, 21, 0.95)' : 'rgba(255, 255, 255, 0.75), rgba(248, 250, 252, 0.95)'}), url('https://images.unsplash.com/photo-1610750009083-0649b0100e84?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-6 md:px-12 py-20">
          <nav className="absolute top-0 left-0 right-0 flex items-center justify-between p-6 md:px-12">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <Car className="w-6 h-6 text-primary-foreground" />
              </div>
              <span className="font-display text-xl font-semibold text-foreground">Curvio</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className="text-foreground hover:text-primary"
                data-testid="landing-theme-toggle-btn"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/auth')}
                    className="text-foreground hover:text-primary"
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/auth?mode=register')}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    data-testid="get-started-btn"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </nav>

          <div className="max-w-3xl mt-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-sm text-primary font-medium">Safe Navigation for Classic Vehicles</span>
            </div>
            
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Navigate with Confidence in Your{' '}
              <span className="text-primary">Oldtimer</span> or{' '}
              <span className="text-primary">Caravan</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl">
              Specialized routing that considers gradients, sharp bends, and narrow roads. 
              Plan your journey knowing every challenge ahead.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register')}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-14 text-lg font-medium active:scale-95 transition-transform"
                data-testid="start-planning-btn"
              >
                Start Planning Routes
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="border-border hover:bg-muted text-foreground px-8 h-14 text-lg"
                data-testid="learn-more-btn"
              >
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 md:px-12 bg-card">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Built for Classic Vehicle Enthusiasts
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Every feature designed with the unique needs of oldtimers, caravans, and motorhomes in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-lg bg-background border border-border hover:-translate-y-1 transition-transform duration-200"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Types Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6">
                Custom Profiles for Your Vehicle
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Create detailed vehicle profiles with dimensions, weight limits, and gradient preferences. 
                Get routes optimized specifically for your vehicle's capabilities.
              </p>
              <div className="space-y-4">
                {['Oldtimer / Classic Cars', 'Caravans', 'Motorhomes'].map((type, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border"
                  >
                    <div className="w-2 h-2 rounded-full bg-primary"></div>
                    <span className="text-foreground font-medium">{type}</span>
                  </div>
                ))}
              </div>
            </div>
            <div 
              className="h-80 md:h-96 rounded-lg bg-cover bg-center"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1746717390936-a1a85d0a36f9?crop=entropy&cs=srgb&fm=jpg&q=85')`
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-card">
        <div className="container mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6">
            Ready to Navigate Smarter?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Join fellow classic vehicle enthusiasts and plan your next adventure with confidence.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate(user ? '/dashboard' : '/auth?mode=register')}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-10 h-14 text-lg font-medium"
            data-testid="cta-get-started-btn"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-border">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-primary" />
            <span className="font-display font-semibold text-foreground">Curvio</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 Curvio. Safe journeys for classic vehicles.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
